import { v4 } from "uuid"
import makeUUID from "@shared/utils/helpers/makeUUID"
import { unwrap } from "@shared/utils/helpers/unwrap"
import { runUntil } from "@shared/utils/helpers/runUntil"
import { createRequest, platform } from "@shared/platform-api/requests"
import { ResolvedVehicleSetEntry, FailedVehicleSetEntry, VehicleSetEntry } from "@shared/platform-api/generated"
import { createRequestHook } from "../../createRequestHook"
import { AddedFleetEntry } from "../../fleet"
import { getResolvedAndUnresolvedEntries } from "./utils"
import { ResolveOutput } from "./types"

type NonResolving = ResolvedVehicleSetEntry | FailedVehicleSetEntry

const isNonResolving = (x: VehicleSetEntry): x is NonResolving => x.status !== `resolving`

const INCLUDE = [
  `abiCode`,
  `abiGroup`,
  `fuelType`,
  `grossWeight`,
  `make`,
  `model`,
  `nominalStartYear`,
  `numberOfSeats`,
  `trimLevel`,
  `type`,
].join(`,`)

export const resolveEntries = createRequest<Readonly<{ entries: ReadonlyArray<AddedFleetEntry> }>, ResolveOutput>(
  async (input, getToken) => {
    const api = platform(getToken)

    const vrns = input.entries.map((x) => x.registrationNumber)
    const vehicleSetId = makeUUID(v4())
    const vehicleSet = await api.vehicles.createVehicleSet({
      requestBody: { id: vehicleSetId, vrns },
    })

    const result = await runUntil<ReadonlyArray<NonResolving>>(async () => {
      const { entries } = await api.vehicles.getVehicleSet({ id: vehicleSet.id, include: INCLUDE })
      return entries.every(isNonResolving) ? entries : null
    })

    if (result === null) {
      throw new Error(`Timeout: Took longer than 30 seconds to resolve vehicles`)
    }

    return {
      ...getResolvedAndUnresolvedEntries(
        input.entries.map((x) => {
          const match = result.find((y) => x.registrationNumber.toUpperCase().replace(/ /g, ``) === y.vrn)

          return match?.status === `resolved`
            ? {
                ...x,
                vehicleId: makeUUID(match.vehicle.id),
                make: unwrap(match.vehicle.make),
                model: unwrap(match.vehicle.model),
                abiGroup: match.vehicle.abiGroup ?? null,
                fuelType: match.vehicle.fuelType ?? null,
                type: unwrap(match.vehicle.type),
                trim: match.vehicle.trimLevel ?? null,
                nominalStartYear: match.vehicle.nominalStartYear ?? null,
                grossWeight: match.vehicle.grossWeight ?? null,
                numberOfSeats: match.vehicle.numberOfSeats ?? null,
                abiCode: match.vehicle.abiCode ?? null,
              }
            : x
        })
      ),
      vehicleSetId,
    }
  }
)

export const useResolveEntries = createRequestHook(resolveEntries)
